import { defineComponent as _defineComponent } from 'vue'
import {
  getAllInOneMachine,
  getEquipmentChangeRecordsList,
  getFiveSixDrillFileType,
  saveEquipmentSetting,
} from '@/apis/equipment'
import type {
  EquipmentType,
  GetChangeRecordsListParams,
  RecordListItemDataType,
} from '@/apis/equipment/type'
import { request } from '@/util/vueRequest'
import axios from 'axios'
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'

import type {
  CatalogDataChildrenType,
  CatalogDataType,
  ChangeRecordDataType,
  ChangeRecordJsonDataType,
} from '../types/changeRecordType'
import {
  EquipmentSaveApis,
  needData,
  pickerOptions,
  tableChangeType,
} from './config'
import RecordTable from './record-table.vue'

// 获取实例, 用于获取路由信息
interface INcLabel {
  id: number
  label_name: string
  label_name_show: string
  label_type: string
  val: string
}

type OptionsType = {
  isLoadingMore?: boolean
  isReset?: boolean
}
// 获取数据
type TableType = keyof typeof equipmentTableMapKey

// 处理变更数据中表格的操作记录
type ParamsType = {
  data: any
  setting_id: number
  password: boolean
  phone?: string
  sms_code?: string
  sms_type?: string
  area_code?: string
  nonce_str?: string
  production_from?: string
  platform?: string
}

// 确定还原参数

export default /*#__PURE__*/_defineComponent({
  __name: 'm-change-record-modal',
  props: {
    visible: { type: Boolean },
    operate_equipment: null
  },
  emits: ["update:visible", "success", "needSaveVerify"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
  (e: 'update:visible', val: boolean): void
  (e: 'success', val: any): void
  (e: 'needSaveVerify'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  visible: boolean // 是否显示弹窗
  operate_equipment: EquipmentType // 操作设备
}

const vm = getCurrentInstance()

const route = vm?.proxy?.$route

const ncLabel = computed<INcLabel[]>(() => vm?.proxy?.$store.state.ncLabel)




// 需要映射成中文的值
const commonMapToChinese = new Map<string | boolean, string>([
  [true, '开启'],
  [false, '关闭'],
])

// 已经在保存时处理了数据，因此此处不再需要添加新的映射值了。(一些特殊情况可以在此处添加)
const specialNeedMapToChinese = new Map<
  string,
  Map<string | number | boolean, string>
>([
  [
    '设备类型',
    new Map([
      ['sixDock', '六面钻'],
      ['fiveDock', '五面钻'],
    ]),
  ],
  [
    '工位起始点',
    new Map<string | boolean, string>([
      ['bottomRight', '右边、下边'],
      ['topLeft', '左边、上边'],
      ['topRight', '右边、上边'],
      ['bottomLeft', '左边、下边'],
      [true, '启动'],
      [false, '关闭'],
    ]),
  ],
  [
    '修边方向',
    new Map([
      ['bottomRight', '右边、下边'],
      ['topLeft', '左边、上边'],
      ['topRight', '右边、上边'],
      ['bottomLeft', '左边、下边'],
    ]),
  ],
  [
    '单面加工板正面生成修边路径且先修边方向',
    new Map([
      ['bottomRight', '右边、下边'],
      ['topLeft', '左边、上边'],
      ['topRight', '右边、上边'],
      ['bottomLeft', '左边、下边'],
    ]),
  ],
  [
    '反面修边顺序',
    new Map([
      ['切割开始', '反面最开始修边'],
      ['切割结束', '反面孔槽加工后修边'],
    ]),
  ],
  [
    '排钻加工模式',
    new Map([
      ['recent_hole', '就近打孔（快速换刀）'],
      ['group_hole', '按类打孔（完成一类孔再换刀）'],
    ]),
  ],
  [
    '组合下刀基准',
    new Map([
      ['main', '以主孔'],
      ['end', '以从孔（尾孔）'],
    ]),
  ],
  ['首刀提前', new Map([[-1, '关闭']])],
  ['第二把提前', new Map([[-1, '关闭']])],
  [
    'mpr侧面孔Z坐标基准面',
    new Map([
      [true, '板件下表面'],
      [false, '板件上表面'],
    ]),
  ],
  [
    '反面槽Z坐标',
    new Map([
      [true, '-(槽深)'],
      [false, '-(板厚-槽深)'],
    ]),
  ],
  [
    '文件编码格式设置',
    new Map([
      ['utf8bom', '带bom的UTF-8'],
      ['utf-8', 'UTF-8'],
      ['gbk', 'ansi'],
    ]),
  ],
  [
    '输出文件格式',
    new Map([
      ['default', ' 输出默认格式文件'],
      ['mpr', '输出MPR格式文件'],
    ]),
  ],
  [
    '文件类型',
    new Map([
      ['newMpr', '新版MPR'],
      ['mpr', '旧版MPR'],
    ]),
  ],
  [
    '圆弧插补',
    new Map([
      [true, '顺时针:G03 逆时针:G02'],
      [false, '顺时针:G02 逆时针:G03'],
    ]),
  ],
  [
    '夹手方向',
    new Map([
      ['L', '左侧'],
      ['R', '右侧'],
      ['T', '上侧'],
      ['B', '下侧'],
    ]),
  ],
])

// 五六面钻文件类型
let fiveSixDirTypeList: {
  dock56Format: string
  modelName: string
}[] = []

// 一体机类型
let oneMachineTypeList: {
  aio_format: string
  model_name: string
}[] = []

// 根据特殊值，判断展示需要映射成中文的数据
const dealSpecialValue = (
  val: string | boolean | number | any[],
  path: string[]
) => {
  const lastPath = path[path.length - 1]

  // 判断这个值是否需要映射成中文
  const key = specialNeedMapToChinese.get(lastPath)
  if (key && !Array.isArray(val)) {
    return key.get(val) || val
  }
  // 通用映射关系
  if (typeof val === 'boolean') {
    return commonMapToChinese.get(val)
  }

  // 生成文件类型（来自于接口数据）
  if (path && path.includes('生成文件类型')) {
    return (
      fiveSixDirTypeList.find((item) => item.dock56Format === val)?.modelName ||
      val
    )
  }
  // 一体机类型
  if (path && path.includes('文件格式')) {
    return (
      oneMachineTypeList.find((item) => item.aio_format === val)?.model_name ||
      val
    )
  }
  // 雕刻机的贴标图片名
  if (
    path &&
    path.includes('贴标图片名') &&
    typeof val === 'string' &&
    Array.isArray(JSON.parse(val))
  ) {
    console.log('ncLabel.value', ncLabel.value)

    return JSON.parse(val).map((name: string) => {
      return (
        ncLabel.value.find((item) => item.label_name === name)
          ?.label_name_show || name
      )
    })
  }
  // 雕刻机的 PDF 文件名（字段映射成中文）
  if (path && path.some((item) => item.includes('PDF文件名'))) {
    const pdfKeyToTextMap = new Map<string, string>([
      ['address', '项目地址'],
      ['orderNo', '订单号'],
      ['firstFolder', '一级文件夹'],
      ['customerName', '客户名称'],
    ])
    try {
      const arr = Array.isArray(val) ? val : JSON.parse(String(val))
      if (Array.isArray(arr)) {
        return arr.map((key) => pdfKeyToTextMap.get(String(key)) || key)
      }
    } catch (error) {}
  }
  return val
}

// 时间筛选条件
const filterTime = ref<string[]>([])
const start_date = ref('')
const end_date = ref('')
// 修改日期的事件
const changeFilterTime = () => {
  // TODO: 发起请求，获取相应数据
  start_date.value = filterTime.value ? filterTime.value[0] : ''
  end_date.value = filterTime.value ? filterTime.value[1] : ''
  page.value = 1
  selectedItem.value = null
  getCatalogueData()
}

// 右侧记录数据
const changeRecords = ref<ChangeRecordDataType[]>([])

// 左侧目录数据
const catalogueData = ref<CatalogDataType[]>([])

// 总的数据条数，用于判断是否还有更多数据
const total = ref(0)
// 当前的数据条数
const currentTotal = ref(0)
const isLoading = ref(false)
const getCatalogueData = async (
  payload = { isLoadingMore: false, isReset: false } as OptionsType
) => {
  const { isLoadingMore, isReset } = payload
  // 重新获取数据，重置之前数据
  if (!isLoadingMore) {
    // 重置数据
    catalogueData.value = []
    changeRecords.value = []
    currentTotal.value = 0
  }
  // 重置数据，将筛选条件置空
  if (isReset) {
    page.value = 1
    start_date.value = ''
    end_date.value = ''
    start_date.value = ''
    end_date.value = ''
    filterTime.value = []
  }
  total.value = 0
  const params: GetChangeRecordsListParams = {
    setting_id: route?.query?.setting_id as unknown as number,
    operate_equipment: props.operate_equipment,
    limit: 15,
    page: page.value,
    start_date: start_date.value,
    end_date: end_date.value,
  }
  try {
    isLoading.value = true
    const res = await getEquipmentChangeRecordsList(params)
    if (res.status === 1) {
      // 处理数据
      const {
        data: { data, total: totalNum },
      } = res
      total.value = totalNum
      if (!isLoadingMore) {
        const { tempData, length } = handleCatalogueData(data)
        catalogueData.value = tempData
        currentTotal.value = length
      } else {
        // 传入老数据， 防止在加载更多时， 相同日期分开的问题
        const { tempData, length } = handleCatalogueData(
          data,
          catalogueData.value
        )
        catalogueData.value = tempData
        currentTotal.value += length
      }
    } else {
      throw new Error('获取数据失败')
    }
    // 选中第一条
    if (!isLoadingMore && catalogueData.value.length) {
      handleSelectItem(
        catalogueData.value[0].children[0],
        catalogueData.value[0].date
      )
    }
  } catch (error) {
    // 加载更多时，如果报错，将page减1
    if (isLoadingMore) {
      page.value = page.value - 1
    }
    vm?.proxy.$message('获取数据失败,请稍后重试')
  } finally {
    isLoading.value = false
  }
}
// 处理左侧目录数据
const handleCatalogueData = (
  data: Record<string, RecordListItemDataType>,
  oldData: CatalogDataType[] = []
) => {
  const tempData = oldData
  let length = 0
  for (const key in data) {
    const item = data[key]
    const date = key.split(' ')[0]
    const time = key.split(' ')[1]
    const operatorPhone = item.operator_phone
    const actionIp = item.action_ip
    length++
    const recordIndex = tempData.findIndex((item) => item.date === date)
    if (recordIndex !== -1) {
      tempData[recordIndex].children.push({
        time,
        id: time,
        data: item,
        operatorPhone,
        actionIp,
      })
    } else {
      tempData.push({
        date,
        children: [
          {
            time,
            id: item.id,
            data: item,
            operatorPhone,
            actionIp,
          },
        ],
      })
    }
  }
  return { tempData, length }
}
const page = ref(1)
// 加载更多
const onLoadMore = () => {
  page.value++
  getCatalogueData({ isLoadingMore: true })
}

const isLoadingRecordData = ref(false)
// 当前选中的item
const selectedItem = ref<(CatalogDataChildrenType & { date: string }) | null>(
  null
)
// 是否来自于柜柜
const isFromGuiGui = ref(false)
const handleSelectItem = (item: CatalogDataChildrenType, date: string) => {
  // 判断当前选中项是否和之前选中项相同
  if (selectedItem.value?.id === item.id) return
  selectedItem.value = { ...item, date }
  handleRecordData()
}
// 选中后处理右侧数据
const handleRecordData = () => {
  // 根据选中的item中的action_url字段，获取对应的修改记录
  if (!selectedItem.value) return
  isFromGuiGui.value = false
  const {
    data: { action_url },
  } = selectedItem.value
  // 来源于柜柜中的保存数据，无需展示
  if (!action_url) {
    isFromGuiGui.value = true
    return
  }
  isLoadingRecordData.value = true
  axios
    .get(action_url)
    .then((res: { status: number; data: ChangeRecordJsonDataType[] }) => {
      if (res.status === 200) {
        const { data } = res
        changeRecords.value = handleDealJsonData(data)
      } else {
        vm?.proxy.$message.error('获取修改记录失败,请稍后重试')
        changeRecords.value = []
      }
    })
    .catch((error) => {
      console.error(error)
    })
    .finally(() => {
      //FIX 太快了，页面像闪烁了一下一样
      setTimeout(() => {
        isLoadingRecordData.value = false
      }, 200)
    })
}

// 处理获取的json数据，将其转换为修改记录数据[{path:[],oldVal:'',newVal:''}]
const handleDealJsonData = (
  data: ChangeRecordJsonDataType | ChangeRecordJsonDataType[]
) => {
  const tempData: ChangeRecordDataType[] = []
  // 采用深度优先遍历
  function dfs(
    data: ChangeRecordJsonDataType | ChangeRecordJsonDataType[],
    path: string[]
  ) {
    if (!data) return
    // 参数归一
    if (Array.isArray(data)) {
      data = data[0]
    }
    // 存在oldValue，即是最后一层
    if (Object.keys(data).includes('oldValue')) {
      // 新旧值相等,这里用===，主要是存在0-》false的情况,还有“”-》非空字符串的情况
      if (data.oldValue === data.value) return
      path.push(data.key)
      tempData.push({
        path,
        oldVal: data.oldValue!,
        newVal: data.value,
      })
    }
    // 处理特殊情况（表格数据）
    if (data.type?.includes('table') && Array.isArray(data.value)) {
      tempData.push({
        path: [...path, data.key],
        type: data.type,
        oldVal: data.oldValue!,
        newVal: data.value,
      })
    }
    // value是数组，且没有oldValue这个字段，继续遍历
    if (Array.isArray(data.value) && !Object.keys(data).includes('oldValue')) {
      for (let i = 0; i < data.value.length; i++) {
        dfs(data.value[i], [...path, data.key])
      }
    }
  }
  dfs(data, [])
  return tempData
}

// 不同设备中表格数据的key值(每个表格数据的type值不同，需要取值的字段也不相同，根据实际情况在此处更改)
const equipmentTableMapKey = {
  engraving: 'name',
  ptpHoleDrill: 'type',
  electronicSaw: 'head',
  sideHoleDrill: 'id',
  knife_store_table: 'name', // 刀库
  layout_settings_table: 'imageFormat', //排版图设置
  customer_form_table: 'importHeader',
  ptp_knife_store_table: 'type', //PTP刀库
} as const

// 表格操作记录中对应的tableType类型
const handleDealTableChangeRecords = (data: {
  type: TableType
  value: any[]
}) => {
  const { type: tableType, value } = data
  const changeRecords: any[] = []
  value.forEach(
    (item: {
      operate_type?: keyof typeof tableChangeType
      name?: string
      type?: string
      head?: string
      id?: string
      imageFormat?: string
      importHeader: string
    }) => {
      if (item.operate_type) {
        const type = tableChangeType[item.operate_type]
        changeRecords.push({
          type,
          value:
            item[equipmentTableMapKey[tableType]] ||
            equipmentTableMapKey[
              props.operate_equipment as unknown as keyof typeof equipmentTableMapKey
            ],
        })
      }
    }
  )
  return changeRecords
}

// 监听弹窗是否显示，显示时获取数据
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      // 获取数据
      getCatalogueData()
    }
  }
)
// 控制弹窗显示
const visible = computed({
  get() {
    return props.visible
  },
  set(val) {
    emits('update:visible', val)
  },
})
// 关闭弹窗
const handleCancel = () => {
  visible.value = false
  resetData()
}
// 关闭时，重置所有数据
const resetData = () => {
  catalogueData.value = []
  changeRecords.value = []
  total.value = 0
  currentTotal.value = 0
  selectedItem.value = null
  filterTime.value = []
  start_date.value = ''
  end_date.value = ''
  page.value = 1
  isLoading.value = false
  isLoadingRecordData.value = false
}

// 控制确认弹窗
const confirmVisible = ref(false)
const confirmTitle = ref('重置参数')
const confirmText = ref('是否将当前设备对接参数重置为默认状态?')
const isSaving = ref(false)
// 记录当前是还原操作还是重置操作
const currentOperation = ref<'reset' | 'reduction'>('reset')
// 点击还原参数按钮
const handleReductionParams = async () => {
  if (!selectedItem.value) return
  const { time, date } = selectedItem.value
  confirmVisible.value = true
  currentOperation.value = 'reduction'
  confirmText.value = `是否将当前设备对接参数还原为${date} ${time}时的保存状态?`
  confirmTitle.value = '还原参数'
}
const handleDealReduction = async () => {
  // 获取当前设备的保存接口
  isSaving.value = true
  let shouldClearSaveInfo = false
  try {
    const url = EquipmentSaveApis[props.operate_equipment]
    let data =
      selectedItem.value?.data.save_data &&
      JSON.parse(selectedItem.value.data.save_data)
    if (typeof data === 'string' && data.startsWith('https')) {
      const jsonData = await request.get(data)
      data = dealJsonData(jsonData?.data)
    }
    const params: ParamsType = {
      data: data,
      setting_id: route?.query?.setting_id as unknown as number,
      password: true,
      ...(props.operate_equipment === 'engraving' ? getSaveVerifyInfo() : {}),
    }
    let newParams = handleDealParams(params)
    const res = await saveEquipmentSetting(url, newParams)
    if (res.data.status === 1) {
      // 保存成功，后续处理
      vm?.proxy.$message.success('还原成功')
      // 关闭弹窗
      confirmVisible.value = false
      if (needData.includes(props.operate_equipment)) {
        emits('success', data)
      } else {
        emits('success', { save_data: data })
      }
      // TODO:重新获取修改记录数据,此处让外界组件调用
      return true
    } else {
      if (props.operate_equipment === 'engraving' && res.data.status === -1) {
        confirmVisible.value = false
        emits('needSaveVerify')
        return false
      }
      shouldClearSaveInfo = true
      throw new Error(res.data?.msg || '还原失败')
    }
  } catch (error: any) {
    vm?.proxy.$message.error(error.toString())
    shouldClearSaveInfo = true
    return false
  } finally {
    isSaving.value = false
    if (shouldClearSaveInfo) {
      vm?.proxy?.$store.commit('setSaveInfo', {})
    }
  }
}

const getSaveVerifyInfo = () => {
  const saveInfo =
    (vm?.proxy?.$store.state.saveInfo as Record<string, any>) || {}
  if (!Object.keys(saveInfo).length) return {}
  return {
    ...saveInfo,
    platform: 'bluen',
  }
}

// 处理调用保存接口的参数问题(不同设备接受到的参数形式不同)
const handleDealParams = (params: ParamsType) => {
  const currentEquipment = props.operate_equipment
  // TODO: 这里返回相同类型数据的时候没做合并处理，便于之后修改
  switch (currentEquipment) {
    case 'electronicSaw':
      return JSON.stringify({
        ...params.data,
        setting_id: params.setting_id,
        password: params.password,
      })
    case 'engraving':
      return JSON.stringify(params)
    case 'glassEquip':
      return params
    case 'dock56FDrill':
      return params.data
    case 'sideHoleDrill':
      return params
    case 'ptpHoleDrill':
      return params.data
    default:
      throw new Error('设备类型错误')
  }
}

// 处理保存的json数据(统一格式)
const dealJsonData = (jsonData: Record<string, any>) => {
  const currentEquipment = props.operate_equipment

  switch (currentEquipment) {
    case 'ptpHoleDrill':
      return JSON.stringify(jsonData)
    case 'sideHoleDrill':
      return jsonData.data
    case 'electronicSaw':
    case 'engraving':
    case 'glassEquip':
    case 'dock56FDrill':
      return jsonData
    default:
      throw new Error('设备类型错误')
  }
}

// 重置参数
const handleResetParams = () => {
  confirmVisible.value = true
  currentOperation.value = 'reset'
  confirmTitle.value = '重置参数'
  confirmText.value = '是否将当前设备对接参数重置为默认状态?'
}
// 确定重置
const handleDealReset = () => {
  // TODO: 发送请求，重新获取修改记录数据
}

// 确认弹窗的confirm事件处理
const handleConfirm = async () => {
  switch (currentOperation.value) {
    // 重置操作
    case 'reset':
      // TODO: 发送请求，重新获取修改记录数据
      handleDealReset()
      break
    // 还原操作
    case 'reduction':
      // TODO:发送请求，重新获取修改记录数据
      handleDealReduction()
      break
  }
}

onMounted(async () => {
  try {
    const [
      {
        data: { model_list: fileTypeList },
      },
      {
        data: { model_list: equipmentList },
      },
    ] = await Promise.all([
      getFiveSixDrillFileType({
        from: 'user',
      }),
      getAllInOneMachine({
        from: 'user',
      }),
    ])
    fiveSixDirTypeList = fileTypeList
    oneMachineTypeList = equipmentList
  } catch (err) {
    if (err?.message === 'canceled') return
    throw err
  }
})

expose({
  getData: getCatalogueData,
  handleDealReduction,
})

return { __sfc: true,vm, route, ncLabel, props, emits, commonMapToChinese, specialNeedMapToChinese, fiveSixDirTypeList, oneMachineTypeList, dealSpecialValue, filterTime, start_date, end_date, changeFilterTime, changeRecords, catalogueData, total, currentTotal, isLoading, getCatalogueData, handleCatalogueData, page, onLoadMore, isLoadingRecordData, selectedItem, isFromGuiGui, handleSelectItem, handleRecordData, handleDealJsonData, equipmentTableMapKey, handleDealTableChangeRecords, visible, handleCancel, resetData, confirmVisible, confirmTitle, confirmText, isSaving, currentOperation, handleReductionParams, handleDealReduction, getSaveVerifyInfo, handleDealParams, dealJsonData, handleResetParams, handleDealReset, handleConfirm, pickerOptions, RecordTable }
}

})